import AccordionTab from "components/util/AccordianTab";
import { Button, Grid, TextField, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { allSelectedData } from "../../../redux/slices/allSelectedDataSlice";
import { clearValidationError } from "../../../redux/slices/formValidationSlice";
import { useParams } from "react-router-dom";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormControl, Select, MenuItem } from '@mui/material';
import dayjs from "dayjs";

const SupportingData: React.FC<Record<any, any>> = (props) => {
  const dispatch = useAppDispatch();
  const selectedData = useAppSelector((state) => state.allSelectedData);
  //const userContext = useAppSelector((state) => state.userContextDetails?.data);
  const userRole = useAppSelector((state) => state.userContextDetails?.data[0]?.role?.toLowerCase());
  const validationErrors = useAppSelector((state) => state.formValidation.errors);
  const { formtype } = useParams();
  const status = useAppSelector((state) => state.allSelectedData.leaseStatus);
  const [fileUrl, setFileUrl] = useState<string>("");
  const [urlError, setUrlError] = useState<string>("");

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleTenantContactAvailableChange = (event: any) => {
    dispatch(allSelectedData({
      ...selectedData,
      tenantContactAvailable: event.target.value
    }));
  };

  const handleSustainabilityContactAvailableChange = (event: any) => {
    dispatch(allSelectedData({
      ...selectedData,
      sustainabilityContactAvailable: event.target.value
    }));
  };

  const handleTenantIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(allSelectedData({
      ...selectedData,
      tenantId: value
    }));

    // Clear validation error when value is entered
    if (value) {
      dispatch(clearValidationError('tenantId'));
    }
  };

  const handleAccentureTeamMemberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(allSelectedData({
      ...selectedData,
      teamMemberId: value
    }));
  };

  const handleLeaseActivatedDateChange = (newValue: any) => {
    dispatch(allSelectedData({ ...selectedData, leaseActivatedDate: newValue ? dayjs(newValue).format('MM-DD-YYYY') : null }));
    if (newValue) {
      dispatch(clearValidationError('leaseActivatedDate'));
    }
  };

  const handleFileUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFileUrl(value);
    // Clear error when input changes
    if (urlError) {
      setUrlError("");
    }
  };

  const handleAddUrl = () => {
    if (!fileUrl.trim()) {
      return;
    }

    // First check if it's a valid URL
    if (!isValidUrl(fileUrl)) {
      setUrlError("Please enter a valid URL (e.g., https://example.com)");
      return;
    }

    // Get current URLs as an array for checking duplicates
    const currentUrls = selectedData.fileName ? selectedData.fileName.split('\n').filter(url => url.trim() !== '') : [];

    // Then check if URL already exists in the list
    if (currentUrls.includes(fileUrl)) {
      setUrlError("This URL has already been added");
      return;
    }

    // If both checks pass, add the URL
    const newFileUrlString = selectedData.fileName ? `${selectedData.fileName}\n${fileUrl}` : fileUrl;
    dispatch(allSelectedData({
      ...selectedData,
      fileName: newFileUrlString
    }));

    // Clear validation error when URLs are added
    if (newFileUrlString) {
      dispatch(clearValidationError('fileUrlList'));
    }

    setFileUrl("");
    setUrlError("");
  };

  const handleUrlClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    dispatch(allSelectedData({
      ...selectedData,
      comments: value
    }));

    // Clear validation error when comment is entered
    if (value) {
      dispatch(clearValidationError('SupportingDataComment'));
    }
  };

  const handleAbstractStatusChange = (event: any, newValue: any) => {
    dispatch(allSelectedData({
      ...selectedData,
      abstractStatus: newValue.props.value
    }));
  }

  const handleSubjectToSLRChange = (event: any, newValue: any) => {
    dispatch(allSelectedData({
      ...selectedData,
      subjectToSLR: newValue.props.value
    }));
  }

  return (
    <AccordionTab
      title={"Supporting Data"}
      children={
        <div>
          <Grid container spacing={2}>
            <Grid item xs={12} md={11}>
              <Grid>
                <label>File URL</label><span className="mandatory"> *</span>
              </Grid>
              <Grid>
                <TextField
                  size="small"
                  fullWidth={true}
                  value={fileUrl}
                  disabled={
                    (formtype === "view" || status === "Lease Activated" || userRole === "asset manager" ||
                      (
                        formtype === "approval" && userRole === "accenture" && (status === "Pending PrM Updates" || status === "Pending PrM Review")

                      ) ||
                      (
                        formtype === "approval" &&
                        ((userRole === "property mgmt accountant" || userRole === "assistant property manager" || userRole === "property manager") &&
                          (status === "Pending Abstraction" || status === "Abstraction in Progress" || status === "Pending Accenture Updates" || status === "Pending Activation"))
                      )
                    )}
                  onChange={handleFileUrlChange}
                  error={Boolean(urlError) || !!validationErrors.fileUrlList}
                  helperText={urlError}
                  placeholder="https://example.com"
                  inputProps={{
                    'aria-label': 'File URL',
                  }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} md={1}>
              <Grid>
                <br></br>
              </Grid>
              <Grid className="button-container">
                <Button
                  variant="contained"
                  fullWidth={true}
                  className={!fileUrl.trim() ? "deactivate-btn" : "add-standard-tenant-btn"}
                  onClick={handleAddUrl}
                  disabled={!fileUrl.trim()}
                >
                  Add
                </Button>
              </Grid>
            </Grid>

            {/* Display URLs */}
            <Grid item xs={12}>
              {selectedData.fileName && (
                <div style={{ marginTop: '16px' }}>
                  {selectedData.fileName.split('\n').filter(url => url.trim() !== '').map((url, index) => (
                    <div
                      key={index}
                      className="url-item"
                    >
                      <a
                        href={url}
                        onClick={(e) => {
                          e.preventDefault();
                          handleUrlClick(url);
                        }}
                        className="url-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {url}
                      </a>
                      <IconButton
                        size="small"
                        disabled={formtype === "view"}
                        onClick={() => {
                          const urlList = selectedData.fileName.split('\n').filter(u => u.trim() !== '');
                          urlList.splice(index, 1);
                          const newUrlString = urlList.join('\n');
                          dispatch(allSelectedData({
                            ...selectedData,
                            fileName: newUrlString
                          }));
                        }}
                        aria-label="delete url"
                        sx={{
                          color: '#ff0000',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
            </Grid>

            <Grid item xs={12} md={12}>
              <Grid>
                <label>Comments</label><span className="mandatory"> *</span>
              </Grid>
              <Grid>
                <TextField
                  size="small"
                  fullWidth={true}
                  multiline
                  minRows={3}
                  disabled={formtype === "view"}
                  maxRows={7}
                  value={selectedData.comments}
                  onChange={handleCommentChange}
                  error={!!validationErrors.SupportingDataComment}
                />
              </Grid>
            </Grid>
          </Grid>
          {((formtype === "view" && status === "Lease Activated") || (formtype === "approval" && ((status === "Pending Activation" && (userRole === "admin" || userRole === "accenture")) || status === "Lease Activated") )) && (
            <Grid container spacing={4} sx={{ mt: 1 }}>
              <Grid item xs={12} md={3}>
                <Grid>
                  <label>Lease Activated Date</label>{status === 'Pending Activation' && <span className="mandatory"> *</span>}
                </Grid>
                <Grid>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      value={selectedData.leaseActivatedDate ? dayjs(selectedData.leaseActivatedDate) : null}
                      disabled={status === "Lease Activated" || !(status === "Pending Activation" && formtype === "approval" && (userRole === "admin" || userRole === "accenture"))}
                      onChange={handleLeaseActivatedDateChange}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          error: !!validationErrors.leaseActivatedDate,
                          helperText: validationErrors.leaseActivatedDate
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              <Grid item xs={12} md={3}>
                <Grid>
                  <label>Tenant ID / Lease ID</label>{status === 'Pending Activation' && <span className="mandatory"> *</span>}
                </Grid>
                <Grid>
                  <TextField
                    size="small"
                    fullWidth={true}
                    inputProps={{
                      min: 0,
                      maxLength: 256,
                      onPaste: (e) => {
                        const pastedText = e.clipboardData.getData('text');
                        if (pastedText.length > 256) {
                          e.preventDefault();
                        }
                      }
                    }}
                    disabled={status === "Lease Activated" || !(status === "Pending Activation" && formtype === "approval" && (userRole === "admin" || userRole === "accenture"))}
                    onChange={handleTenantIdChange}
                    value={selectedData.tenantId}
                    error={!!validationErrors.tenantId}
                    helperText={validationErrors.tenantId}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12} md={3}>
                <Grid>
                  <label>Tenant contact available?</label>
                </Grid>
                <Grid>
                  <RadioGroup
                    row
                    aria-label="tenantContactAvailable"
                    name="tenantContactAvailable"
                    value={selectedData?.tenantContactAvailable || "No"}
                    onChange={handleTenantContactAvailableChange}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio color="primary" />}
                      label="Yes"
                      disabled={status === "Lease Activated" || !(status === "Pending Activation" && formtype === "approval" && (userRole === "admin" || userRole === "accenture"))}
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio color="primary" />}
                      label="No"
                      disabled={status === "Lease Activated" || !(status === "Pending Activation" && formtype === "approval" && (userRole === "admin" || userRole === "accenture"))}
                    />
                  </RadioGroup>
                </Grid>
              </Grid>
              <Grid item xs={12} md={3}>
                <Grid>
                  <label>Sustainability contact available?</label>
                </Grid>
                <RadioGroup
                  row
                  aria-label="sustainabilityContactAvailable"
                  name="sustainabilityContactAvailable"
                  value={selectedData?.sustainabilityContactAvailable || "No"}
                  onChange={handleSustainabilityContactAvailableChange}

                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio color="primary" />}
                    label="Yes"
                    disabled={status === "Lease Activated" || !(status === "Pending Activation" && formtype === "approval" && (userRole === "admin" || userRole === "accenture"))}
                  />
                  <FormControlLabel
                    value="No"
                    control={<Radio color="primary" />}
                    label="No"
                    disabled={status === "Lease Activated" || !(status === "Pending Activation" && formtype === "approval" && (userRole === "admin" || userRole === "accenture"))}
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          )}
          {((formtype === "view" || formtype === "approval") && status?.toLowerCase() !== "saved" && status?.toLowerCase() !== "pending abstraction" )  && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Grid>
                  <label>Abstract Status</label>{<span className="mandatory"> *</span>}
                </Grid>
                <Grid>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={selectedData?.abstractStatus || ''}
                      displayEmpty
                      disabled={!(formtype === "approval" && status?.toLowerCase() === 'abstraction in progress' && (userRole?.toLowerCase() === 'accenture' || userRole?.toLowerCase() === 'admin'))}
                      error={!!validationErrors.abstractStatus}
                      onChange={handleAbstractStatusChange}
                    >
                      <MenuItem value="Current">Current</MenuItem>
                      <MenuItem value="Ready">Ready</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <Grid>
                  <label>Subject To SLR</label>{<span className="mandatory"> *</span>}
                </Grid>
                <Grid>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={selectedData?.subjectToSLR || ''}
                      displayEmpty
                      disabled={!(formtype === "approval" && status?.toLowerCase() === 'abstraction in progress' && (userRole?.toLowerCase() === 'accenture' || userRole?.toLowerCase() === 'admin'))}
                      onChange={handleSubjectToSLRChange}
                      error={!!validationErrors.subjectToSLR}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <Grid>
                  <label>Accenture Team Member (Email)</label>{<span className="mandatory"> *</span>}
                </Grid>
                <Grid>
                  <TextField
                    size="small"
                    fullWidth={true}
                    inputProps={{
                      min: 0,
                      maxLength: 256,
                      onPaste: (e) => {
                        const pastedText = e.clipboardData.getData('text');
                        if (pastedText.length > 256) {
                          e.preventDefault();
                        }
                      }
                    }}
                    disabled={!(formtype === "approval" && status?.toLowerCase() === 'abstraction in progress' && (userRole?.toLowerCase() === 'accenture' || userRole?.toLowerCase() === 'admin'))}
                    onChange={handleAccentureTeamMemberChange}
                    value={selectedData.teamMemberId}
                    error={!!validationErrors.teamMemberId}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </div>
      }
    />
  );
};

export default SupportingData;
